import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CreditCard, Landmark, Loader2, Lock } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Option } from "@/pages/portal/PayPage";
import { Expired, Frame, ResultView } from "@/pages/PayLink";

const EXPIRED = "This payoff link has expired — please sign in or request a new payoff letter";
const pub = axios.create({ baseURL: `${API}/payofflink` });

const Figure = ({ label, value, testid, strong }) => (
  <div>
    <div className="eyebrow text-[10px]">{label}</div>
    <div className={`num mt-1 font-display font-semibold ${strong ? "text-4xl" : "text-2xl text-muted-foreground"}`} data-testid={testid}>{value}</div>
  </div>
);

export function PayoffLinkPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [expired, setExpired] = useState(false);
  const [method, setMethod] = useState("ach");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { pub.get(`/${token}`).then((r) => setD(r.data)).catch(() => setExpired(true)); }, [token]);

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await pub.post(`/${token}/checkout`, { method, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (e) {
      if (e.response?.status === 410) setExpired(true);
      setErr(e.response?.data?.detail?.message || "Couldn't start the payment. Please try again.");
      setBusy(false);
    }
  };

  if (expired) return <Frame><Expired message={EXPIRED} testid="payofflink-expired" /></Frame>;
  if (!d) return <Frame><div className="py-16 text-center"><Loader2 className="mx-auto animate-spin" /></div></Frame>;
  return (
    <Frame>
      <div data-testid="payofflink-page">
        <div className="eyebrow" data-testid="payofflink-loan-number">{d.loan_number} · Secure payoff</div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight" data-testid="payofflink-greeting">Hi {d.first_name}, pay off your loan</h1>
        <div className="gold-rule mb-6 mt-4 w-24" />
        <div className="mb-6 grid gap-5 rounded-lg border bg-card p-5 sm:grid-cols-2">
          <Figure label="Payoff today" value={money(d.amount_cents)} testid="payofflink-amount" strong />
          <Figure label={`Quoted, good through ${fmtDate(d.good_through)}`} value={money(d.quoted_cents)} testid="payofflink-quoted" />
          <div className="text-xs text-muted-foreground sm:col-span-2" data-testid="payofflink-today-payoff">
            Today's ledger payoff: {money(d.today_payoff_cents)}. You're charged today's payoff — never more than the quoted amount.
          </div>
          {d.pending.map((t, i) => (
            <div key={i} className="text-xs font-medium text-[hsl(var(--gold))] sm:col-span-2" data-testid={`payofflink-pending-${i}`}>
              Bank payment of {money(t.amount_cents)} started {fmtDate(t.started_on)} · {t.message} (already excluded)
            </div>
          ))}
        </div>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary" data-testid="payofflink-bank-tip"><Landmark className="h-4 w-4" /> Save time and fees: pay by bank account</p>
        <div className="space-y-3" role="radiogroup">
          <Option active={method === "ach"} onClick={() => setMethod("ach")} testid="payofflink-method-ach"
            title={<span className="flex flex-wrap items-center gap-2">Pay by bank account — free <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">Recommended</span></span>}>
            <div className="text-xs text-muted-foreground">No fee. Usually clears in 3–5 business days.</div>
          </Option>
          <Option active={method === "card"} onClick={() => setMethod("card")} testid="payofflink-method-card" title="Pay by card">
            <div className="text-xs text-muted-foreground">Debit or credit card. No fee.</div>
          </Option>
        </div>
        {err && <p className="mt-3 text-sm text-destructive" data-testid="payofflink-error">{err}</p>}
        <Button onClick={go} disabled={busy || d.amount_cents < 100} className="mt-6 h-12 w-full text-base" data-testid="payofflink-continue-button">
          {busy ? <Loader2 className="animate-spin" /> : method === "ach" ? <Landmark /> : <CreditCard />} Pay {money(d.amount_cents)} securely
        </Button>
        <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground"><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This link only lets you pay off this loan. Sign in to see your full account.</p>
      </div>
    </Frame>
  );
}

export const PayoffLinkResult = () => <ResultView client={pub} expiredMessage={EXPIRED} />;
